import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimWorkOrderChangeObjectSelectionDataFilterInput, BimWorkOrderObjectSelectionData } from 'gql/graphql';
import type { TConstructionTypesIndexes } from 'pages/Project/Common/types';
import { defaultCounts } from 'pages/Project/Common/WorkOrderStructure/consts';

import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { bimWorkOrderChangeObjectSelectionListIsIncludedGql } from '../bimWorkOrderChangeStructureQueries';

interface IUseIncludedCountByConstructionType {
    workOrderChangeId?: number;
}

const sumReducer = (a: number, i: any) => a + (i.isIncluded ? 1 : 0);

const mapInitialData = (initialData: BimWorkOrderObjectSelectionData[]) =>
    Object.keys(defaultCounts).reduce((acc, key) => {
        acc[key as TConstructionTypesIndexes] = initialData
            .filter((i) => String(i.constructionType.id) === key)
            .reduce(sumReducer, 0);

        return acc;
    }, defaultCounts);

const requestId = 'bimWorkOrderChangeObjectSelectionListIsIncluded';

export const useIncludedChangeCountByConstructionType = (props: IUseIncludedCountByConstructionType) => {
    const dispatch = useDispatch();
    const { workOrderChangeId } = props;

    const [includedCount, setIncludedCount] = useState<Record<TConstructionTypesIndexes, number>>();

    const [
        bimWorkOrderObjectSelectionListIsIncludedData,
        bimWorkOrderObjectSelectionListIsIncludedStatus,
        ,
        bimWorkOrderObjectSelectionListIsIncludedRequest,
    ] = useRequest({
        requestId,
        query: bimWorkOrderChangeObjectSelectionListIsIncludedGql,
        withEffect: false,
    });

    const [bimWorkOrderObjectSelectionListIsIncludedReset] = useResetStoreData({ requestId });

    const refreshIncludedCount = useCallback(
        (constructionTypeId: number) => {
            const where: BimWorkOrderChangeObjectSelectionDataFilterInput = {
                constructionType: { id: { eq: constructionTypeId } },
            };

            dispatch(bimWorkOrderObjectSelectionListIsIncludedRequest({ workOrderChangeId, where }));
        },
        [bimWorkOrderObjectSelectionListIsIncludedRequest, dispatch, workOrderChangeId],
    );

    useEffect(() => {
        if (
            bimWorkOrderObjectSelectionListIsIncludedStatus === RequestStatus.SUCCESS &&
            bimWorkOrderObjectSelectionListIsIncludedData?.bimWorkOrderChangeObjectSelectionList?.items?.length
        ) {
            const items = bimWorkOrderObjectSelectionListIsIncludedData?.bimWorkOrderChangeObjectSelectionList?.items;

            if (includedCount) {
                const constructionTypeId = items[0].constructionType.id;

                setIncludedCount({
                    ...includedCount,
                    [constructionTypeId]: items.reduce(sumReducer, 0),
                });
            } else {
                setIncludedCount(mapInitialData(items as BimWorkOrderObjectSelectionData[]));
            }

            bimWorkOrderObjectSelectionListIsIncludedReset(undefined);
        }
    }, [
        bimWorkOrderObjectSelectionListIsIncludedData?.bimWorkOrderChangeObjectSelectionList?.items,
        bimWorkOrderObjectSelectionListIsIncludedReset,
        bimWorkOrderObjectSelectionListIsIncludedStatus,
        includedCount,
    ]);

    useEffect(() => {
        dispatch(bimWorkOrderObjectSelectionListIsIncludedRequest({ workOrderChangeId }));
    }, [
        bimWorkOrderObjectSelectionListIsIncludedRequest,
        bimWorkOrderObjectSelectionListIsIncludedReset,
        dispatch,
        workOrderChangeId,
    ]);

    return { includedCount, refreshIncludedCount, setIncludedCount };
};
